import { Helmet } from "react-helmet";
import { useState, useMemo } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import DashboardGrid from "@/components/dashboard/DashboardGrid";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useDashboardSettings } from "@/hooks/useDashboardSettings";
import { DashboardSettingsDialog } from "@/components/dashboard/DashboardSettingsDialog";
import { useDashboardData } from "@/hooks/useDashboardData";
import { DashboardData } from "@/types/dashboard";

export default function Dashboard() {
  const { t } = useLanguage();
  const [timeRange, setTimeRange] = useState(30);
  const {
    settings,
    toggleCard,
    toggleMetric,
    setRansomwareFilters,
    setNewsSources,
    setReleaseProducts,
    setEolProducts,
    setLayoutOrder,
    setCardSize,
    resetToDefaults
  } = useDashboardSettings();

  // Get data to extract available options
  const { data } = useDashboardData(timeRange);
  const dashboardData = data as DashboardData;

  // Extract available options from data
  const availableCountries = useMemo(() => {
    const countries = new Set<string>();
    (dashboardData?.allRansomwareAttacks || []).forEach((attack) => {
      if (attack.country && attack.country !== '??') {
        countries.add(attack.country);
      }
    });
    return Array.from(countries).sort();
  }, [dashboardData?.allRansomwareAttacks]);

  const availableGroups = useMemo(() => {
    const groups = new Set<string>();
    (dashboardData?.allRansomwareAttacks || []).forEach((attack) => {
      if (attack.group) {
        groups.add(attack.group);
      }
    });
    return Array.from(groups).sort();
  }, [dashboardData?.allRansomwareAttacks]);

  const availableSources = useMemo(() => {
    const en = new Set<string>();
    const pl = new Set<string>();

    (dashboardData?.allNews || []).forEach((news) => {
      if (news.source) {
        if (news.lang === 'pl') {
          pl.add(news.source);
        } else {
          en.add(news.source);
        }
      }
    });

    return {
      en: Array.from(en).sort(),
      pl: Array.from(pl).sort()
    };
  }, [dashboardData?.allNews]);

  const availableProducts = useMemo(() => {
    const products = new Set<string>();
    (dashboardData?.allReleases || []).forEach((release) => {
      if (release.nazwa_projektu) {
        products.add(release.nazwa_projektu);
      }
    });
    return Array.from(products).sort();
  }, [dashboardData?.allReleases]);

  const availableEolProducts = useMemo(() => {
    const products = new Set<string>();
    (dashboardData?.upcomingEOL || []).forEach((item) => {
      if (item.name) {
        products.add(item.name);
      }
    });
    return Array.from(products).sort();
  }, [dashboardData?.upcomingEOL]);

  const timeRanges = [30, 60, 90, 180];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Helmet>
        <title>{t('dashboard.pageTitle')}</title>
        <meta name="description" content={t('dashboard.description')} />
      </Helmet>
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-24 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 text-center"
        >
          <h1 className="text-4xl font-black tracking-tight sm:text-6xl mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-500">
            {t('dashboard.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
            {t('dashboard.subtitle').replace('{days}', timeRange.toString())}
          </p>

          <div className="flex justify-center items-center gap-2 mb-8">
            {timeRanges.map((days) => (
              <Button
                key={days}
                variant={timeRange === days ? "default" : "outline"}
                onClick={() => setTimeRange(days)}
                className="min-w-[80px]"
              >
                {days} {t('days') || 'dni'}
              </Button>
            ))}
            <DashboardSettingsDialog
              settings={settings}
              onToggleCard={toggleCard}
              onToggleMetric={toggleMetric}
              onSetRansomwareFilters={setRansomwareFilters}
              onSetNewsSources={setNewsSources}
              onSetReleaseProducts={setReleaseProducts}
              onSetEolProducts={setEolProducts}
              onReset={() => {
                resetToDefaults();
                setTimeRange(30);
              }}
              availableCountries={availableCountries}
              availableGroups={availableGroups}
              availableSourcesEN={availableSources.en}
              availableSourcesPL={availableSources.pl}
              availableProducts={availableProducts}
              availableEolProducts={availableEolProducts}
            />
          </div>
        </motion.div>

        <DashboardGrid
          days={timeRange}
          settings={settings}
          onLayoutChange={setLayoutOrder}
          onCardSizeChange={setCardSize}
        />
      </main>
      <Footer />
    </div>
  );
}
